// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ItemDetail, ItemField } from '@/items/api/types';

// Define the mergeable fields with their types
type MergeableField = 'name' | 'sku';

/**
 * Type-safe field accessor for mergeable item fields
 */
function getMergeableFieldValue(
  item: ItemDetail,
  field: MergeableField
): string | null {
  switch (field) {
    case 'name':
      return item.name;
    case 'sku':
      return item.sku;
    default:
      // TypeScript will ensure this is never reached
      return null;
  }
}

/**
 * Determines the merged field value based on selection
 */
export function getMergedFieldValue(
  field: string,
  primaryItem: ItemDetail,
  selectedItem: ItemDetail,
  fieldSelections: Record<string, 'primary' | 'selected'>
): string | null {
  if (!isMergeableField(field)) {
    return null;
  }

  const selection = fieldSelections[field];
  const primaryValue = getMergeableFieldValue(primaryItem, field);
  const selectedValue = getMergeableFieldValue(selectedItem, field);

  if (selection === 'primary') {
    return primaryValue || selectedValue;
  }
  return selectedValue || primaryValue;
}

/**
 * Type guard to check if a field is mergeable
 */
function isMergeableField(field: string): field is MergeableField {
  return field === 'name' || field === 'sku';
}

/**
 * Determines if radio buttons should be shown for a field
 */
export function shouldShowFieldRadio(
  field: string,
  primaryItem: ItemDetail,
  selectedItem: ItemDetail
): boolean {
  if (!isMergeableField(field)) {
    return false;
  }

  const primaryValue = getMergeableFieldValue(primaryItem, field);
  const selectedValue = getMergeableFieldValue(selectedItem, field);
  return (
    (Boolean(primaryValue) || Boolean(selectedValue)) &&
    primaryValue !== selectedValue
  );
}

/**
 * Initializes field selections with smart defaults
 */
export function initializeFieldSelections(
  primaryItem: ItemDetail,
  selectedItem: ItemDetail
): Record<string, 'primary' | 'selected'> {
  const initialSelections: Record<string, 'primary' | 'selected'> = {};
  const fields: MergeableField[] = ['name', 'sku'];

  fields.forEach((field) => {
    const primaryValue = getMergeableFieldValue(primaryItem, field);
    const selectedValue = getMergeableFieldValue(selectedItem, field);

    // If retained item is null/not set, use the removed item as default
    if (!primaryValue && selectedValue) {
      initialSelections[field] = 'selected';
    } else {
      initialSelections[field] = 'primary';
    }
  });

  // Initialize BOM selection - if retained item has no BOM, use removed item
  const primaryHasBOM = primaryItem.bom && primaryItem.bom.length > 0;
  const selectedHasBOM = selectedItem.bom && selectedItem.bom.length > 0;
  if (!primaryHasBOM && selectedHasBOM) {
    initialSelections.bom = 'selected';
  } else {
    initialSelections.bom = 'primary';
  }

  return initialSelections;
}

/**
 * Prepares field updates based on selections
 */
export function prepareFieldUpdates(
  primaryItem: ItemDetail,
  selectedItem: ItemDetail,
  fieldSelections: Record<string, 'primary' | 'selected'>
): Record<string, string | null> {
  const fieldUpdates: Record<string, string | null> = {};
  const fields: MergeableField[] = ['name', 'sku'];

  fields.forEach((field) => {
    const selection = fieldSelections[field];
    const primaryValue = getMergeableFieldValue(primaryItem, field);
    const selectedValue = getMergeableFieldValue(selectedItem, field);

    if (selection === 'primary') {
      fieldUpdates[field] = primaryValue || selectedValue;
    } else {
      fieldUpdates[field] = selectedValue || primaryValue;
    }
  });

  return fieldUpdates;
}

/**
 * Gets unique field labels from both items (case-insensitive)
 */
export function getUniqueFieldLabels(
  primaryFields: ItemField[],
  selectedFields: ItemField[]
): string[] {
  const allFieldLabels = new Set<string>();
  primaryFields.forEach((field) =>
    allFieldLabels.add(field.label.toLowerCase())
  );
  selectedFields.forEach((field) =>
    allFieldLabels.add(field.label.toLowerCase())
  );
  return Array.from(allFieldLabels).sort();
}

/**
 * Creates a map of fields by lowercase label for easy lookup
 */
export function createFieldMap(fields: ItemField[]): Map<string, ItemField> {
  const fieldMap = new Map<string, ItemField>();
  fields.forEach((field) => {
    fieldMap.set(field.label.toLowerCase(), field);
  });
  return fieldMap;
}
